import {
  CSSProperties,
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import MiniAudioPlayer from "../../../components/media/MiniAudioPlayer";
import { FLAG_AUDIO_MINI_PLAYER } from "../../../config/featureFlags";
import { InternalAudioPlayer } from "../../../objects/audio";
import { AnalyticsItemList } from "../../../tools/analytics/analytics.types";
import {
  gaEventArticleAudioProgress,
  gaEventArticlePlayAudio,
  gaEventViewItemArticle,
} from "../../../tools/analytics/domains/article.analytics";
import {
  gaEventPlayPodcastEpisode,
  gaEventPodcastEpisodeAudioProgress,
  gaEventViewItemPodcastEpisode,
} from "../../../tools/analytics/domains/podcast.analytics";
import { isIOSApp } from "../../../tools/ios";
import { isDesktopShell } from "../../../common/hooks/useIsDesktopView";
import {
  getDurationRatioFromPlayedChunks,
  getPlayedPercent,
} from "../../audio/utils/getDurationRatioFromPlayedChunks";
import {
  useContentProgress,
  type ContentProgressSubject,
} from "../../../tools/analytics/useContentProgress";
import { DebugLogger } from "../utils/debugLogger";
import { useFirebaseUser } from "../../user/hooks/useFirebaseUser";
import { useCurrentUser } from "../../user/hooks/useCurrentUser";
import { isInternalRoom } from "../../../lib/helpers";
import type { ApiPodcastEpisode } from "../../podcast/hooks/useGetPodcastsListQuery";
import { useApiQuery, type Api } from "../../../services/api";
import type { ArticleDetail } from "../../../features/article/hooks/useArticleData";

type ArticleListItem =
  Api["get"]["/webapp/public/articles"]["output"]["body"]["docs"][number];

type ApiArticleAudioSource = ArticleDetail | ArticleListItem;

type PlaylistAudioContent = Extract<
  Api["get"]["/webapp/public/content/:id"]["output"]["body"],
  { content_format: "Audio" }
>;

type AudioMediaDataBase = {
  title: string;
  trackUrl: string;
  imageUrl: string;
};

export type AudioMediaDataModel =
  | (AudioMediaDataBase & {
      type: "article_audio";
      source: ApiArticleAudioSource;
    })
  | (AudioMediaDataBase & {
      type: "podcast_episode";
      source: ApiPodcastEpisode;
    })
  | (AudioMediaDataBase & {
      type: "playlist_audio_content";
      source: PlaylistAudioContent;
    });

/**
 * The track on the common consumption scale. A playlist's audio content is
 * not one of its types.
 */
function consumedAudio(
  content: AudioMediaDataModel
): ContentProgressSubject | null {
  switch (content.type) {
    case "article_audio":
      return { contentType: "article_audio", contentId: content.source.id };
    case "podcast_episode":
      return { contentType: "podcast_episode", contentId: content.source.id };
    case "playlist_audio_content":
      return null;
    default:
      throw new Error(content satisfies never);
  }
}

const debug = new DebugLogger("AudioContext");
debug.mute();

/**
 * Where the bytes of the current track come from. A `live-stream` is still
 * being synthesized: it has no length and cannot seek, so the player polls for
 * that stream's own saved file and switches to it, at the same position, once
 * the synthesis is whole.
 */
export type TrackSource =
  | { kind: "file" }
  | { kind: "live-stream"; articleId: string; streamId: string };

type LiveStream = Extract<TrackSource, { kind: "live-stream" }> & {
  startedAt: number;
  /** The track generation this stream was loaded under. */
  generation: number;
};

// Synthesis runs 2-4x faster than playback, so the file usually exists within
// the first minute of listening.
const FINISHED_FILE_POLL_MS = 3000;
// The file may never come (a stream cut mid-way is not saved). Past this, the
// listener keeps the live stream without seeking, and the polling stops.
const FINISHED_FILE_POLL_LIMIT_MS = 10 * 60_000;
// A file load normally reports its duration in under 2 s.
const SWITCH_LOAD_TIMEOUT_MS = 20_000;

type SetTracksPayload = {
  contentList: AudioMediaDataModel[];
  index: number;
  /** Where the bytes come from — a finished file, or a stream still being
   *  synthesized. Required: a caller that does not say cannot be defaulted
   *  safely, since only the native shell can consume a stream. */
  track: TrackSource;
  autoplay?: boolean;
  onLoad?: (duration: number) => void;
  onPause?: () => void;
  onEndReached?: () => void;
};

const AudioContext = createContext<{
  currentMediaContent: AudioMediaDataModel | null;
  player: InternalAudioPlayer | null;
  hasTrack: boolean;
  duration: number;
  isPlaying: boolean;
  currentTime: number;
  playbackRate: number;
  canPlayPrevious: boolean;
  canPlayNext: boolean;
  /** False while a live stream plays: it has no length and no byte ranges,
   *  so seeking and skipping wait for the finished file. */
  canSeek: boolean;
  /** Playback percentage, or null when the track has no knowable length
   *  (a live stream still being synthesized). */
  progressRate: number | null;
  trackTitle: string;
  trackImageUrl: string;
  trackCollectionName: string;
  isAudioPlayerPanelOpen: boolean;
  isInfoPanelVisible: boolean;
  miniPlayerStyles: CSSProperties;
  setTracks: (payload: SetTracksPayload) => void;
  setPlaybackRate: (rate: number) => void;
  play: () => void;
  pause: () => void;
  togglePlayPause: () => void;
  stop: () => void;
  seek: (time: number) => void;
  clear: () => void;
  skipForward: (time: number) => void;
  skipBackward: (time: number) => void;
  previous: () => void;
  next: () => void;
  setTrackTitle: (title: string) => void;
  setTrackImageUrl: (url: string) => void;
  setTrackCollectionName: (name: string) => void;
  setAudioPlayerPanelOpen: (isOpen: boolean) => void;
  setInfoPanelVisible: (isVisible: boolean) => void;
  updateMiniPlayerStyles: (styles: CSSProperties) => void;
  hideMiniPlayer: () => void;
  unhideMiniPlayer: () => void;
  moveMiniPlayerOutOfScreen: () => void;
  moveMiniPlayerBackOnScreen: () => void;
}>({
  currentMediaContent: null,
  player: null,
  hasTrack: false,
  duration: 0,
  isPlaying: false,
  currentTime: 0,
  playbackRate: 0,
  canPlayPrevious: false,
  canPlayNext: false,
  canSeek: true,
  progressRate: null,
  trackTitle: "",
  trackImageUrl: "",
  trackCollectionName: "",
  isAudioPlayerPanelOpen: true,
  isInfoPanelVisible: false,
  miniPlayerStyles: {},
  setTracks: () => {},
  setPlaybackRate: () => {},
  play: () => {},
  pause: () => {},
  togglePlayPause: () => {},
  stop: () => {},
  seek: () => {},
  clear: () => {},
  skipForward: () => {},
  skipBackward: () => {},
  previous: () => {},
  next: () => {},
  setTrackTitle: () => {},
  setTrackImageUrl: () => {},
  setTrackCollectionName: () => {},
  setAudioPlayerPanelOpen: () => {},
  setInfoPanelVisible: () => {},
  updateMiniPlayerStyles: (_styles: CSSProperties) => {},
  hideMiniPlayer: () => {},
  unhideMiniPlayer: () => {},
  moveMiniPlayerOutOfScreen: () => {},
  moveMiniPlayerBackOnScreen: () => {},
});

export function useAudioContext() {
  return useContext(AudioContext);
}

// The id of the loaded content alone. Cards that mark the content "Playing"
// read it here: the main context changes on every playback tick, and a feed of
// cards re-rendering four times a second would be the price of one badge.
const LoadedAudioIdContext = createContext<string | null>(null);

/**
 * The id of the article whose narration is loaded in the desktop sidebar
 * player (playing or paused), or null. Always null on the phone, whose floating player already
 * says what plays.
 */
export function useLoadedAudioId(): string | null {
  return useContext(LoadedAudioIdContext);
}

export function AudioProvider({ children }: { children: ReactNode }) {
  const { isAuthenticatedFirebase: isAuthenticated } = useFirebaseUser();
  const { currentUser } = useCurrentUser();
  const [trackList, setTrackList] = useState<AudioMediaDataModel[]>([]);
  const [trackIndex, setTrackIndex] = useState(0);
  const [currentTrackUrl, setCurrentTrackUrl] = useState("");
  const [currentTime, setCurrentTime] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(1);
  const playerRef = useRef<InternalAudioPlayer | null>(null);
  const [isPlaying, setPlaying] = useState(false);
  const [trackTitle, setTrackTitle] = useState("");
  const [trackImageUrl, setTrackImageUrl] = useState("");
  const [trackCollectionName, setTrackCollectionName] = useState("");
  const [isAudioPlayerPanelOpen, setAudioPlayerPanelOpen] = useState(false);
  const [miniPlayerStyles, setMiniPlayerStyles] = useState<CSSProperties>({});
  const [memMiniPlayerBottom, setMemMiniPlayerBottom] = useState<
    number | string | undefined
  >();
  const [isInfoPanelVisible, setInfoPanelVisible] = useState(false);
  const [hasPlayedTrackOnce, setPlayedTrackOnce] = useState(false);
  const progressRateRef = useRef<number | null>(0);
  const lastProgressStepRef = useRef(0);
  const trackDurationRef = useRef(-1);
  const [trackDuration, setTrackDuration] = useState(0);
  // Non-null while the current track is a live stream still being synthesized.
  const [liveStream, setLiveStream] = useState<LiveStream | null>(null);
  const canSeek = liveStream === null;
  // Read by the switch to the finished file, which runs across awaits and must
  // see the latest position, play state and speed, not the ones it captured.
  const currentTimeRef = useRef(0);
  const isPlayingRef = useRef(false);
  const playbackRateRef = useRef(1);
  // Bumped whenever the loaded track changes or is cleared: a switch to the
  // finished file that started under an older generation stops at its next
  // await instead of loading over the new track.
  const trackGenerationRef = useRef(0);
  // The live stream whose switch to its finished file has started, if any.
  const switchedStreamIdRef = useRef<string | null>(null);
  const documentInteractedWithRef = useRef(false);
  const hasTrack = currentTrackUrl !== "";
  const currentMediaContent = trackList[trackIndex] ?? null;
  // The length of the open track, kept past `resetRefValues`, which a track
  // change runs before the open of the previous one ends.
  const openTrackDurationRef = useRef<number | null>(null);
  const audioProgress = useContentProgress({
    subject:
      currentMediaContent === null || !hasTrack
        ? null
        : consumedAudio(currentMediaContent),
    endOnHidden: false,
    onStart: (start) => {
      if (start === "resumed") return;
      openTrackDurationRef.current = null;
    },
    onSettled: null,
    onFinish: () => ({
      mediaDurationSeconds:
        openTrackDurationRef.current === null
          ? null
          : Math.round(openTrackDurationRef.current),
      playlistItems: null,
      article: null,
    }),
  });
  const canPlayPrevious = trackIndex > 0;
  const canPlayNext = trackIndex < trackList.length - 1;

  useEffect(() => {
    const handleClick = () => {
      documentInteractedWithRef.current = true;
    };
    document.addEventListener("click", handleClick);

    return () => {
      document.removeEventListener("click", handleClick);
    };
  }, []);

  useEffect(() => {
    return () => {
      debug.log("Unmounting audio context");
      handleStop();
      resetRefValues();
      playerRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!isAuthenticated && hasTrack) {
      debug.log("Stop player if room content and not authenticated");
      clear();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (isAudioPlayerPanelOpen) {
      setAudioPlayerPanelOpen(false);
    }

    if (
      location.pathname.includes("login") ||
      location.pathname.includes("signup")
    ) {
      debug.log("Stop player if on auth pages");
      clear();
    } else if (location.pathname.includes("/chat")) {
      debug.log("Stop player on chat pages");
      clear();
    } else if (
      location.pathname.includes("profile") ||
      location.pathname.includes("gpt")
    ) {
      debug.log("Hide player");
      hideMiniPlayer();
    } else {
      if (miniPlayerStyles.display === "none") {
        debug.log("Unhide player");
        unhideMiniPlayer();
      }
    }
  }, []);

  const triggerProgressStepEvent = useCallback(
    (content: AudioMediaDataModel) => {
      const percentage = lastProgressStepRef.current;

      switch (content.type) {
        case "article_audio": {
          gaEventArticleAudioProgress({
            article: content.source,
            listName: AnalyticsItemList.ARTICLE_PAGE,
            percentage,
            duration: trackDurationRef.current,
          });
          break;
        }
        case "podcast_episode": {
          gaEventPodcastEpisodeAudioProgress({
            episode: content.source,
            listName: AnalyticsItemList.ROOM_PODCAST,
            percentage,
            duration: trackDurationRef.current,
            internal: isInternalRoom(currentUser, ""),
          });
          break;
        }
        default: {
          console.error("Content type not supported", { content });
          break;
        }
      }
    },
    [currentUser]
  );

  const resetRefValues = useCallback(() => {
    progressRateRef.current = 0;
    lastProgressStepRef.current = 0;
    trackDurationRef.current = -1;
    setTrackDuration(0);
    // NOTE: do NOT reset `documentInteractedWithRef` here. It records whether
    // the user has interacted with the document (browser sticky activation) and
    // gates autoplay. `setTracks` calls this on every load; resetting it broke
    // autoplay for the async path (streamed narration): the document `click`
    // fires and sets the flag true *before* the awaited `synthesize()` resolves
    // and calls `setTracks`, so the reset then cleared it and the later
    // `canplay`/autoplay check saw `false`. Once the user has interacted,
    // autoplay stays permitted for the session — matching the browser.
  }, []);

  const handleStop = useCallback(() => {
    playerRef.current?.stop();
    setPlaying(false);
    isPlayingRef.current = false;
  }, []);

  const clear = useCallback(() => {
    audioProgress.noteExit("back");
    handleStop();
    resetRefValues();
    setCurrentTrackUrl("");
    // Drop the track list too, not just the url. `currentMediaContent` derives
    // from `trackList[trackIndex]` while `hasTrack` derives from
    // `currentTrackUrl`: leaving the list behind lets the two disagree, and a
    // closed player still reports the last article as "loaded". Callers then
    // take the resume branch (`togglePlayPause`) instead of restarting, which
    // flips `isPlaying` back to true over a player `handleStop` already
    // destroyed — the Listen button sticks on "Pause" with no audio and no way
    // back. One teardown, one empty state.
    setTrackList([]);
    setTrackIndex(0);
    setInfoPanelVisible(false);
    setLiveStream(null);
    trackGenerationRef.current++;
  }, [audioProgress.noteExit, handleStop, resetRefValues]);

  const handlePlay = useCallback(
    (content: AudioMediaDataModel | null = currentMediaContent) => {
      playerRef.current?.play();
      setPlaying(true);
      isPlayingRef.current = true;

      if (content && !hasPlayedTrackOnce) {
        if (content.type === "article_audio") {
          gaEventArticlePlayAudio({
            article: content.source,
            listName: AnalyticsItemList.ARTICLE_PAGE,
            duration: trackDurationRef.current,
          });
        }
        if (content.type === "podcast_episode") {
          gaEventPlayPodcastEpisode({
            episode: content.source,
            listName: AnalyticsItemList.ROOM_PODCAST,
            duration: trackDurationRef.current,
            internal: isInternalRoom(currentUser, ""),
          });
        }
        setPlayedTrackOnce(true);
      }
    },
    [currentMediaContent, hasPlayedTrackOnce, currentUser]
  );

  const handlePause = useCallback(() => {
    playerRef.current?.pause();
    setPlaying(false);
    isPlayingRef.current = false;
  }, []);

  const setTracks = useCallback(
    async (payload: SetTracksPayload) => {
      const {
        contentList,
        index,
        track,
        autoplay,
        onLoad,
        onPause,
        onEndReached,
      } = payload;

      const content = contentList[index];
      if (content === undefined) {
        debug.log("Invalid track index", index);
        return;
      }

      if (!playerRef.current) {
        debug.log("Creating new player");
        playerRef.current = new InternalAudioPlayer(isIOSApp());
      }
      resetRefValues();
      playerRef.current.onPlay = () => {
        handlePlay();
      };
      playerRef.current.onPause = () => {
        handlePause();
        onPause?.();
      };

      setTrackList(contentList);
      setTrackIndex(index);
      setTrackTitle(content.title);
      setTrackImageUrl(content.imageUrl);

      if (content.trackUrl === currentTrackUrl) {
        if (autoplay && documentInteractedWithRef.current) handlePlay(content);
      } else {
        // Bumped before the stop, so a switch to a finished file that is
        // mid-flight sees the change at its next await, not after this load.
        trackGenerationRef.current++;
        await playerRef.current.stop();

        debug.log("Loading new track", content.trackUrl);
        playerRef.current.setTrack(content.trackUrl, track.kind);
        setLiveStream(
          track.kind === "live-stream"
            ? {
                ...track,
                startedAt: Date.now(),
                generation: trackGenerationRef.current,
              }
            : null
        );
        const loadedPlayer = playerRef.current;
        playerRef.current._onTrackLoaded = (duration) => {
          trackDurationRef.current = duration;
          setTrackDuration(duration);
          // A new native player starts at 1x; keep the speed the listener set.
          loadedPlayer.setPlaybackRate(playbackRateRef.current);
          onLoad && onLoad(duration);

          if (autoplay && documentInteractedWithRef.current) {
            handlePlay(content);
          }
        };

        setCurrentTrackUrl(content.trackUrl);
        setPlayedTrackOnce(false);
        lastProgressStepRef.current = 0;
      }

      playerRef.current.onProgress = (time: number) => {
        setCurrentTime(time);
        currentTimeRef.current = time;
        // A live stream reports duration 0: there is no total to divide by,
        // and doing it anyway yields NaN (at time 0) or Infinity->100, which
        // pinned the scrubber to "finished" the moment playback started.
        // null says "no fraction exists" so the UI can omit it.
        const totalDuration = trackDurationRef.current;
        progressRateRef.current =
          totalDuration > 0
            ? Math.min(Math.ceil((time / totalDuration) * 100), 100)
            : null;

        if (totalDuration > 0) openTrackDurationRef.current = totalDuration;

        const chunks = playerRef.current?._playedChunks;
        if (chunks) {
          audioProgress.reportPercent(getPlayedPercent(chunks, totalDuration));

          const currentProgressStep = getDurationRatioFromPlayedChunks(
            chunks,
            trackDurationRef.current
          );

          if (currentProgressStep > lastProgressStepRef.current) {
            lastProgressStepRef.current = currentProgressStep;
            triggerProgressStepEvent(content);
          }
        }
      };

      playerRef.current.onEnd = () => {
        // The last progress callback reads the chunks before the player adds
        // the final stretch: the end reports the whole of them.
        const endedPlayer = playerRef.current;
        if (endedPlayer !== null) {
          audioProgress.reportPercent(
            getPlayedPercent(
              endedPlayer._playedChunks,
              trackDurationRef.current
            )
          );
        }
        handlePause();
        onEndReached && onEndReached();
      };
    },
    [
      handlePlay,
      handlePause,
      currentTrackUrl,
      resetRefValues,
      triggerProgressStepEvent,
      audioProgress.reportPercent,
    ]
  );

  const triggerViewItemEvent = useCallback(() => {
    if (!currentMediaContent) return;
    switch (currentMediaContent.type) {
      case "article_audio": {
        // Not congress-attributed: this fires when the AUDIO PLAYER starts a
        // track, which is not a navigation out of a card, so there is no
        // stamped origin to read. Audio attribution is out of scope with
        // podcasts — it needs its own design, not a null threaded to here.
        gaEventViewItemArticle(
          {
            article: currentMediaContent.source,
            listName: AnalyticsItemList.ARTICLE_PAGE,
          },
          null
        );
        break;
      }
      case "podcast_episode": {
        gaEventViewItemPodcastEpisode({
          episode: currentMediaContent.source,
          listName: AnalyticsItemList.ROOM_PODCAST,
        });
        break;
      }
      default: {
        console.error("Content type not supported", { currentMediaContent });
        break;
      }
    }
  }, [currentMediaContent]);

  const handleSeek = useCallback(
    (value: number) => {
      // A live stream has no length and no byte ranges to seek into; asking
      // the native shell anyway crashed the app (AudioStreaming divides by the
      // unknown duration). Seeking resumes once the finished file takes over.
      if (!canSeek) return;
      const player = playerRef.current;
      if (player === null) return;
      player.seek(value);
      setCurrentTime(value);
      currentTimeRef.current = value;
    },
    [canSeek]
  );

  // While a live stream plays, poll for its own saved file (see TrackSource).
  // Stops once the file exists, or after FINISHED_FILE_POLL_LIMIT_MS.
  const finishedFileRefetchInterval = useCallback(
    (data: { body: { url: string | null } } | undefined) =>
      liveStream === null ||
      (data !== undefined && data.body.url !== null) ||
      Date.now() - liveStream.startedAt > FINISHED_FILE_POLL_LIMIT_MS
        ? false
        : FINISHED_FILE_POLL_MS,
    [liveStream]
  );
  const finishedFileQuery = useApiQuery(
    "/webapp/private/articles/:id/speech/stream/:streamId",
    liveStream === null
      ? false
      : {
          param: { id: liveStream.articleId, streamId: liveStream.streamId },
          staleTime: 0,
          refetchInterval: finishedFileRefetchInterval,
        }
  );
  const finishedFileUrl =
    finishedFileQuery.state === "success" ? finishedFileQuery.body.url : null;

  // Switch to the finished file at the current position so the listener can
  // seek. Synthesis outruns playback, so this happens well before the end.
  useEffect(() => {
    if (liveStream === null || finishedFileUrl === null) return;
    // The poll result can flicker (a refetch on focus or reconnect, an error
    // hiding the cached body) and re-run this effect: one switch per stream.
    if (switchedStreamIdRef.current === liveStream.streamId) return;
    // `setTracks` bumps the generation, awaits `stop()`, and only then
    // replaces `liveStream`. A file found in that gap belongs to the stream
    // being replaced: compare against the generation the stream was loaded
    // under, not the current one, or the switch would load it over the new
    // track.
    const generation = liveStream.generation;
    const isCurrent = (): boolean => trackGenerationRef.current === generation;
    if (!isCurrent()) return;
    const player = playerRef.current;
    if (player === null) return;
    switchedStreamIdRef.current = liveStream.streamId;
    const streamUrl = currentTrackUrl;
    const resumeAt = currentTimeRef.current;
    // `liveStream` stays set, so `canSeek` stays false, until the file is
    // loaded and positioned: a seek in between would land on the live stream
    // (a native crash) or be undone by the `seek(resumeAt)` below.

    // The live stream is stopped before the file loads (one native player), so
    // a load that throws or never reports would leave dead audio behind locked
    // controls. Close the player instead: the file exists now, so Listen plays
    // it directly.
    let loaded = false;
    const giveUp = (): void => {
      if (!loaded && isCurrent()) clear();
    };
    const loadTimer = setTimeout(giveUp, SWITCH_LOAD_TIMEOUT_MS);

    void (async () => {
      await player.stop();
      if (!isCurrent()) return;
      player._onTrackLoaded = (duration) => {
        if (!isCurrent()) return;
        loaded = true;
        clearTimeout(loadTimer);
        trackDurationRef.current = duration;
        setTrackDuration(duration);
        progressRateRef.current = Math.min(
          Math.ceil((resumeAt / duration) * 100),
          100
        );
        player.setPlaybackRate(playbackRateRef.current);
        player.seek(resumeAt);
        // Read now, not when the switch began: the listener may have paused.
        if (isPlayingRef.current) void player.play();
        setLiveStream(null);
      };
      try {
        await player.setTrack(finishedFileUrl, "file");
      } catch {
        clearTimeout(loadTimer);
        giveUp();
        return;
      }
      if (!isCurrent()) return;
      setCurrentTrackUrl(finishedFileUrl);
      setTrackList((list) =>
        list.map((content) =>
          content.trackUrl === streamUrl
            ? { ...content, trackUrl: finishedFileUrl }
            : content
        )
      );
    })();
  }, [finishedFileUrl]);

  const handlePrevious = useCallback(() => {
    if (canPlayPrevious) {
      resetRefValues();
      setTracks({
        track: { kind: "file" },
        contentList: trackList,
        index: trackIndex - 1,
        autoplay: true,
      });

      if (isAudioPlayerPanelOpen) triggerViewItemEvent();
    }
  }, [
    canPlayPrevious,
    resetRefValues,
    setTracks,
    trackList,
    trackIndex,
    isAudioPlayerPanelOpen,
    triggerViewItemEvent,
  ]);

  const handleNext = useCallback(() => {
    if (canPlayNext) {
      resetRefValues();
      setTracks({
        track: { kind: "file" },
        contentList: trackList,
        index: trackIndex + 1,
        autoplay: true,
      });

      if (isAudioPlayerPanelOpen) triggerViewItemEvent();
    }
  }, [
    canPlayNext,
    resetRefValues,
    setTracks,
    trackList,
    trackIndex,
    isAudioPlayerPanelOpen,
    triggerViewItemEvent,
  ]);

  const handleChangePlaybackRate = useCallback((rate: number) => {
    playerRef.current?.setPlaybackRate(rate);
    setPlaybackRate(rate);
    playbackRateRef.current = rate;
  }, []);

  const updateMiniPlayerStyles = useCallback((newStyles: CSSProperties) => {
    setMiniPlayerStyles((prevStyles: CSSProperties) => {
      const updatedStyles = {
        ...prevStyles,
        ...newStyles,
      };

      // Shallow equality check to prevent unnecessary state updates
      const hasChanged = Object.keys(updatedStyles).some(
        (key) =>
          updatedStyles[key as keyof CSSProperties] !==
          prevStyles[key as keyof CSSProperties]
      );

      return hasChanged ? updatedStyles : prevStyles;
    });
  }, []);

  const hideMiniPlayer = useCallback(() => {
    setMemMiniPlayerBottom(miniPlayerStyles.bottom);
    updateMiniPlayerStyles({
      display: "none",
    });
  }, [miniPlayerStyles.bottom, updateMiniPlayerStyles]);

  const unhideMiniPlayer = useCallback(() => {
    updateMiniPlayerStyles({
      bottom: memMiniPlayerBottom,
      display: "flex",
    });
  }, [memMiniPlayerBottom, updateMiniPlayerStyles]);

  const moveMiniPlayerOutOfScreen = useCallback(() => {
    setMemMiniPlayerBottom(miniPlayerStyles.bottom);
    updateMiniPlayerStyles({
      bottom: -250,
    });
  }, [miniPlayerStyles.bottom, updateMiniPlayerStyles]);

  const moveMiniPlayerBackOnScreen = useCallback(() => {
    updateMiniPlayerStyles({
      bottom: memMiniPlayerBottom,
    });
  }, [memMiniPlayerBottom, updateMiniPlayerStyles]);

  const handleSetTracks = useCallback(setTracks, [
    currentTrackUrl,
    handlePlay,
    handlePause,
    triggerProgressStepEvent,
  ]);

  const handleSetPlaybackRate = useCallback(handleChangePlaybackRate, [
    handleChangePlaybackRate,
  ]);

  const handleClear = useCallback(clear, [handleStop]);

  const handleTogglePlayPause = useCallback(() => {
    isPlaying ? handlePause() : handlePlay();
  }, [isPlaying, handlePause, handlePlay]);

  const handleSkipForward = useCallback(
    (time: number) => {
      handleSeek(Math.min(currentTime + time, trackDurationRef.current));
    },
    [currentTime, handleSeek]
  );

  const handleSkipBackward = useCallback(
    (time: number) => {
      handleSeek(Math.max(currentTime - time, 0));
    },
    [currentTime, handleSeek]
  );

  const contextValue = useMemo(
    () => ({
      currentMediaContent,
      player: playerRef.current,
      hasTrack,
      duration: trackDuration,
      isPlaying,
      currentTime,
      playbackRate,
      canPlayPrevious,
      canPlayNext,
      canSeek,
      progressRate: progressRateRef.current,
      trackTitle,
      trackImageUrl,
      trackCollectionName,
      isAudioPlayerPanelOpen,
      isInfoPanelVisible,
      miniPlayerStyles,
      setTracks: handleSetTracks,
      setPlaybackRate: handleSetPlaybackRate,
      play: handlePlay,
      pause: handlePause,
      togglePlayPause: handleTogglePlayPause,
      stop: handleStop,
      seek: handleSeek,
      clear: handleClear,
      skipForward: handleSkipForward,
      skipBackward: handleSkipBackward,
      previous: handlePrevious,
      next: handleNext,
      setTrackTitle,
      setTrackImageUrl,
      setTrackCollectionName,
      setAudioPlayerPanelOpen,
      setInfoPanelVisible,
      updateMiniPlayerStyles,
      hideMiniPlayer,
      unhideMiniPlayer,
      moveMiniPlayerOutOfScreen,
      moveMiniPlayerBackOnScreen,
    }),
    [
      currentMediaContent,
      hasTrack,
      trackDuration,
      isPlaying,
      currentTime,
      playbackRate,
      canPlayPrevious,
      canPlayNext,
      canSeek,
      trackTitle,
      trackImageUrl,
      trackCollectionName,
      isAudioPlayerPanelOpen,
      isInfoPanelVisible,
      miniPlayerStyles,
      handleSetTracks,
      handleSetPlaybackRate,
      handlePlay,
      handlePause,
      handleTogglePlayPause,
      handleStop,
      handleSeek,
      handleClear,
      handleSkipForward,
      handleSkipBackward,
      handlePrevious,
      handleNext,
      updateMiniPlayerStyles,
      hideMiniPlayer,
      unhideMiniPlayer,
      moveMiniPlayerOutOfScreen,
      moveMiniPlayerBackOnScreen,
    ]
  );

  return (
    <AudioContext.Provider value={contextValue}>
      <LoadedAudioIdContext.Provider
        value={
          isDesktopShell() &&
          hasTrack &&
          currentMediaContent !== null &&
          currentMediaContent.type === "article_audio"
            ? currentMediaContent.source.id
            : null
        }
      >
        {children}

        {/* The AudioPlayer owns both its mini and expanded states (and its own
            expand/collapse animation, plus the inline info view), staying
            mounted throughout. The desktop shell plays in its sidebar
            instead (AppSidebarAudioPlayer), or the layout mounts this one where
            it has no sidebar. */}
        {FLAG_AUDIO_MINI_PLAYER && !isDesktopShell() && <MiniAudioPlayer />}
      </LoadedAudioIdContext.Provider>
    </AudioContext.Provider>
  );
}
